import { useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = {
  ultimate: { name: 'Ultimate', priceMo: 14.99, accent: 'from-fuchsia-500 to-pink-500', text: 'text-pink-300',   border: 'border-pink-400/60',   glow: 'shadow-[0_0_30px_rgba(236,72,153,0.4)]' },
  premium:  { name: 'Premium',  priceMo:  9.99, accent: 'from-yellow-500 to-amber-500', text: 'text-yellow-200', border: 'border-yellow-400/60', glow: 'shadow-[0_0_30px_rgba(245,158,11,0.4)]' },
  basic:    { name: 'Basic',    priceMo:  0.00, accent: 'from-violet-500 to-indigo-500', text: 'text-violet-200', border: 'border-violet-400/60', glow: 'shadow-[0_0_30px_rgba(139,92,246,0.4)]' },
};

const BENEFITS = [
  { label: 'Ad-free experience',           basic: false, premium: true,    ultimate: true },
  { label: 'AI chat messages',             basic: '250/day', premium: 'Unlimited', ultimate: 'Unlimited' },
  { label: 'Memory Vault (cross-chat)',    basic: false, premium: true,    ultimate: true },
  { label: 'Advanced AI models',           basic: false, premium: true,    ultimate: true },
  { label: 'Highest-tier AI models',       basic: false, premium: false,   ultimate: true },
  { label: 'Image generation',             basic: '10/mo', premium: '25/mo', ultimate: 'Unlimited' },
  { label: 'Priority response speed',      basic: false, premium: true,    ultimate: true },
  { label: 'Custom personas',              basic: '3',   premium: '10',    ultimate: 'Unlimited' },
  { label: 'NSFW unlocked',                basic: true,  premium: true,    ultimate: true },
  { label: 'Premium-only characters',      basic: false, premium: true,    ultimate: true },
  { label: 'Early access to new features', basic: false, premium: false,   ultimate: true },
  { label: 'Founder badge on profile',     basic: false, premium: false,   ultimate: true },
];

function Cell({ value }) {
  if (value === true)  return <Check className="w-4 h-4 text-emerald-400 mx-auto" />;
  if (value === false) return <X className="w-4 h-4 text-white/20 mx-auto" />;
  return <span className="text-xs font-bold text-white block text-center">{value}</span>;
}

export default function Membership() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get('plan') || 'ultimate';
  const [tab, setTab] = useState(PLANS[initial] ? initial : 'ultimate');
  const [cycle, setCycle] = useState('monthly'); // 'monthly' | 'yearly'
  const plan = PLANS[tab];
  const yearlyPrice = useMemo(() => Math.round(plan.priceMo * 12 * 0.7), [plan.priceMo]);
  const yearlyFull = useMemo(() => Math.round(plan.priceMo * 12), [plan.priceMo]);
  const savePct = plan.priceMo > 0 ? Math.round((1 - yearlyPrice / yearlyFull) * 100) : 0;
  const [loading, setLoading] = useState(false);

  // Force monthly for the Basic (free) tab so the toggle never confuses things
  const effectiveCycle = tab === 'basic' ? 'monthly' : cycle;

  const handleSubscribe = async () => {
    if (tab === 'basic') return navigate('/');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/subscription/checkout`,
        { tier: tab, origin_url: window.location.origin, billing_cycle: effectiveCycle },
        { withCredentials: true }
      );
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const ctaPriceLabel =
    tab === 'basic'
      ? ''
      : effectiveCycle === 'yearly'
        ? ` · £${yearlyPrice}/yr`
        : ` · £${plan.priceMo}/mo`;

  return (
    // pb-44 (mobile) gives space so content scrolls past the fixed CTA + bottom nav
    <div className="min-h-screen text-white pb-44 lg:pb-32 bg-gradient-to-b from-[#1a0e2e] via-[#100817] to-[#050505]" data-testid="membership-page">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center gap-3 px-4 pb-3 bg-gradient-to-b from-[#1a0e2e] to-transparent" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-white/70 hover:text-white rounded-full" data-testid="membership-back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="flex-1 text-center font-black text-lg tracking-tight font-['Unbounded']">Member Center</h1>
        <div className="w-9" />
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 px-4 pt-2 border-b border-white/5">
        {Object.entries(PLANS).map(([k, p]) => (
          <button key={k} onClick={() => setTab(k)} className={`relative pb-3 font-black text-base font-['Unbounded'] transition-colors ${tab === k ? p.text : 'text-white/40 hover:text-white/70'}`} data-testid={`membership-tab-${k}`}>
            {p.name}
            {tab === k && <span className={`absolute -bottom-px left-1/4 right-1/4 h-0.5 bg-gradient-to-r ${p.accent} rounded-full`} />}
          </button>
        ))}
      </div>

      {/* Billing cycle — Monthly / Yearly clickable cards */}
      <div className="px-4 pt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setCycle('monthly')}
          disabled={tab === 'basic'}
          className={`relative text-left p-4 rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50 ${
            effectiveCycle === 'monthly'
              ? `bg-gradient-to-br ${plan.accent} bg-opacity-10 border-2 ${plan.border} ${plan.glow}`
              : 'bg-white/[0.04] border border-white/10 hover:border-white/25'
          }`}
          data-testid="cycle-monthly"
        >
          <p className="text-xs text-white/70 mb-1">Per Month</p>
          <p className="text-3xl font-black font-['Unbounded']">
            {plan.priceMo === 0 ? 'Free' : `£${plan.priceMo}`}
          </p>
          {effectiveCycle === 'monthly' && plan.priceMo > 0 && (
            <Check className="absolute top-2 right-2 w-4 h-4 text-white" />
          )}
        </button>

        <button
          type="button"
          onClick={() => setCycle('yearly')}
          disabled={tab === 'basic'}
          className={`relative text-left p-4 rounded-2xl transition-all active:scale-[0.98] disabled:opacity-50 ${
            effectiveCycle === 'yearly'
              ? `bg-gradient-to-br ${plan.accent} bg-opacity-10 border-2 ${plan.border} ${plan.glow}`
              : 'bg-white/[0.04] border border-white/10 hover:border-white/25'
          }`}
          data-testid="cycle-yearly"
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-xs text-white/70">Per Year</p>
            {savePct > 0 && (
              <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                Save {savePct}%
              </span>
            )}
          </div>
          <p className="text-3xl font-black font-['Unbounded'] text-white">
            {plan.priceMo === 0 ? '—' : `£${yearlyPrice}`}
          </p>
          {plan.priceMo > 0 && (
            <p className="text-[10px] text-white/40 line-through mt-0.5">£{yearlyFull}</p>
          )}
          {effectiveCycle === 'yearly' && plan.priceMo > 0 && (
            <Check className="absolute top-2 right-2 w-4 h-4 text-white" />
          )}
        </button>
      </div>

      {/* Benefits table */}
      <div className="mx-4 mt-6 bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[1fr_4.5rem_4.5rem_4.5rem] px-3 py-3 border-b border-white/5 text-[10px] tracking-widest uppercase font-bold text-white/50">
          <span>Benefit</span>
          <span className={`text-center ${tab === 'ultimate' ? 'text-pink-300' : ''}`}>Ult</span>
          <span className={`text-center ${tab === 'premium' ? 'text-yellow-300' : ''}`}>Pre</span>
          <span className={`text-center ${tab === 'basic' ? 'text-violet-300' : ''}`}>Bas</span>
        </div>
        {BENEFITS.map((b, i) => (
          <div key={b.label} className={`grid grid-cols-[1fr_4.5rem_4.5rem_4.5rem] px-3 py-3 items-center ${i < BENEFITS.length - 1 ? 'border-b border-white/[0.04]' : ''}`} data-testid={`benefit-${i}`}>
            <span className="text-sm text-white">{b.label}</span>
            <div className={tab === 'ultimate' ? '' : 'opacity-50'}><Cell value={b.ultimate} /></div>
            <div className={tab === 'premium' ? '' : 'opacity-50'}><Cell value={b.premium} /></div>
            <div className={tab === 'basic' ? '' : 'opacity-50'}><Cell value={b.basic} /></div>
          </div>
        ))}
      </div>

      {/* Subscribe CTA — sits above mobile bottom-nav (bottom-16) and at bottom on desktop (lg:bottom-0) */}
      <div
        className="fixed left-0 right-0 bottom-16 lg:bottom-0 px-4 pb-4 pt-4 bg-gradient-to-t from-[#050505] via-[#050505]/95 to-transparent z-40"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={handleSubscribe}
          disabled={loading}
          className={`w-full py-4 rounded-full font-black text-base text-black bg-gradient-to-r ${plan.accent} hover:opacity-90 transition-opacity ${plan.glow} disabled:opacity-60`}
          data-testid="subscribe-cta"
        >
          {loading
            ? 'Loading…'
            : tab === 'basic'
              ? 'You are on Basic'
              : `Subscribe to ${plan.name}${ctaPriceLabel}`}
        </button>
        <p className="mt-2 text-center text-[10px] text-white/40">
          <Link to="/privacy" className="hover:text-white/70">Privacy</Link>  ·  <Link to="/terms" className="hover:text-white/70">Terms</Link>  ·  <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" className="hover:text-white/70">EULA</a>
        </p>
      </div>
    </div>
  );
}
